/**
 * Elise AI Chatbot Block
 *
 * Global injection block that loads the Elise AI chat widget.
 * Configurable organization, building token, and position.
 * The building token defaults to a property data field so each
 * property automatically gets its own chat instance.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';

function EliseChatbotComponent(_props: BlockProps) {
  console.log('EliseChatbotComponent', _props);
  const org = useBlockToken('organization', {
    defaultField: 'custom_eliseai_organization_id',
  });
  const building = useBlockToken('building_token', {
    defaultField: 'custom_eliseai_building_token',
  });
  const side = useBlockToken('position_side', { defaultValue: 'right' });
  const offsetBottom = useBlockToken('offset_bottom', { defaultValue: '20' });
  const offsetSide = useBlockToken('offset_side', { defaultValue: '20' });

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!building) return;
    (async () => {
      try {
        const existing = document.querySelector('.meetelise-chat');
        if (existing) existing.remove();
        // @ts-ignore — runtime CDN import, not a resolvable module
        const mod = await import(/* @vite-ignore */ 'https://cdn.eliseai.com/@meetelise/chat');
        if (!mountedRef.current) return;
        const position: Record<string, string> = { bottom: `${offsetBottom}px` };
        if (side === 'left') {
          position.left = `${offsetSide}px`;
        } else {
          position.right = `${offsetSide}px`;
        }

        const result = mod.default.start({
          organization: org,
          building,
          position,
        });
        console.log('result', result);
      } catch (err) {
        console.warn('[EliseChatbot] Failed to load Elise SDK:', err);
      }
    })();

    return () => {
      mountedRef.current = false;
      const el = document.querySelector('.meetelise-chat');
      if (el) el.remove();
    };
  }, [org, building, side, offsetBottom, offsetSide]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'EliseChatbot',
  displayName: 'Elise AI Chatbot',
  tokens: {
    organization: {
      label: 'Organization',
      type: 'text',
      defaultField: 'custom_eliseai_organization_id',
      suggestedFields: ['custom_eliseai_organization_id'],
      description: 'Elise AI organization identifier.',
    },
    building_token: {
      label: 'Building Token',
      type: 'text',
      defaultField: 'custom_eliseai_building_token',
      suggestedFields: ['custom_eliseai_building_token'],
      description: 'Property-specific building token from Elise AI.',
      required: true,
    },
    position_side: {
      label: 'Side',
      type: 'text',
      defaultValue: 'right',
      description: 'Which side of the screen the widget appears on ("left" or "right").',
    },
    offset_bottom: {
      label: 'Bottom Offset (px)',
      type: 'text',
      defaultValue: '20',
      description: 'Distance from the bottom of the viewport in pixels.',
    },
    offset_side: {
      label: 'Side Offset (px)',
      type: 'text',
      defaultValue: '20',
      description: 'Distance from the left or right edge in pixels.',
    },
  },
};

export const eliseChatbotBlock: BlockDefinition = {
  id: 'elise-chatbot',
  name: 'Elise AI Chatbot',
  description: 'Floating AI leasing chatbot powered by Elise AI. Automatically connects to the property via building token.',
  category: 'Integrations',
  placement: 'injection',
  component: EliseChatbotComponent,
  contentSchema,
  tags: ['chatbot', 'elise', 'ai', 'chat', 'leasing', 'lead'],
};
